import { i18n } from '@lingui/core';
import type { RangeFilter as TRangeFilter } from '@schibsted-nmp/search-frontend-clients/quest';
import { Button } from '@warp-ds/react';
import React from 'react';
import { useSubmitSearch } from '../../../data/SearchQuery';
import { RangeInput } from './RangeInput';
import { useRangeValues } from './useRangeValues';

type RangeFilterProps = {
    filter: TRangeFilter;
};

export function RangeFilter(props: RangeFilterProps) {
    const { filter } = props;

    const { lowerValue, upperValue, setRangeValues } = useRangeValues(filter);
    const { submitSearch } = useSubmitSearch();

    const handleChange = React.useCallback(
        (name: string, value: string) => {
            if (name === filter.name_from) {
                setRangeValues({ lower: value });
            } else {
                setRangeValues({ upper: value });
            }
        },
        [setRangeValues, filter],
    );

    function handleSubmit(ev: React.FormEvent) {
        // Prevent page reload on form submit
        ev.preventDefault();

        if (isValidRange(lowerValue, upperValue)) {
            submitSearch((urlParams) => {
                lowerValue
                    ? urlParams.set(filter.name_from, lowerValue)
                    : urlParams.delete(filter.name_from);
                upperValue
                    ? urlParams.set(filter.name_to, upperValue)
                    : urlParams.delete(filter.name_to);
            });
        }
    }

    const isInvalid = !isValidRange(lowerValue, upperValue);

    return (
        <form onSubmit={handleSubmit}>
            <div className="flex items-start space-x-16">
                <RangeInput
                    ariaLabel={i18n.t({
                        id: 'filters.range.minimum.label',
                        message: '{filterName}, minimum',
                        values: { filterName: filter.display_name },
                    })}
                    helpText={i18n.t({
                        id: 'filters.range.minimum.help',
                        message: 'From {unit}',
                        values: { unit: filter.unit },
                    })}
                    invalid={isInvalid}
                    name={filter.name_from}
                    onChange={handleChange}
                    value={lowerValue}
                />
                <RangeInput
                    ariaLabel={i18n.t({
                        id: 'filters.range.maximum.label',
                        message: '{filterName}, maximum',
                        values: { filterName: filter.display_name },
                    })}
                    helpText={i18n.t({
                        id: 'filters.range.maximum.help',
                        message: 'To {unit}',
                        values: { unit: filter.unit },
                    })}
                    invalid={isInvalid}
                    name={filter.name_to}
                    onChange={handleChange}
                    value={upperValue}
                />
                <Button type="submit">
                    {i18n.t({
                        id: 'filters.range.button.search',
                        message: 'Search',
                    })}
                </Button>
            </div>
            {isInvalid && (
                <div className="text-xs mt-4 block i-text-$color-helptext-text i-text-$color-helptext-text-negative">
                    <span>
                        {i18n.t({
                            id: 'filters.range.invalid',
                            message:
                                '"From" value must be lower than "To" value.',
                        })}
                    </span>
                </div>
            )}
        </form>
    );
}

function isValidRange(lowerValue: string, upperValue: string) {
    const isInvalid =
        Boolean(lowerValue) &&
        Boolean(upperValue) &&
        Number(lowerValue) > Number(upperValue);
    return !isInvalid;
}
